import { createContext, useCallback, useContext, useEffect } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { Text } from '@chakra-ui/react'
import { MODAL_TYPES, ProductTypes, SubscriptionType } from '@editor/config/constants'
import { UserContextProps } from '@editor/interfaces/interfaces'
import { ERROR_PROCESS, processErrorHandler } from '@editor/services/ErrorHandler'
import { getUser } from '@editor/services/Users'
import { decideUserType } from '@editor/services/Validation'
import { RootState, useAppDispatch } from '@editor/store/app'
import {
  setHasDeprecatedSubscription,
  setIsAllowedToDebug,
  setUser,
  setUserLoaded,
  setUserProfile,
  setUserType,
} from '@editor/store/user'
import { MODE } from '@infraloop/services'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'

import { GlobalModalContext } from './GlobalModal'

//* アプリ全体としてユーザー情報を管理するプロバイダーの生成
export const UserContext = createContext<UserContextProps>({
  getAccessToken: async () => Promise.resolve(''),
})

//* プロバイダーに渡すユーザー情報の項目や操作処理
export function useUserContext(): UserContextProps {
  const { showErrorModal, showModal } = useContext(GlobalModalContext)
  const { error, logout } = useAuth0()

  const handleUserNotFound = useCallback(() => {
    showModal({
      body: (
        <>
          <Text>3D InfraLoopのアカウントを持っていないため、ログアウトします。</Text>
          <Text>
            3D InfraLoopの閲覧用アカウントを作成するには、右上の
            <b>「新規登録」</b>
            ボタンを押してください。
          </Text>
          <Text>
            有償のアカウントを作成するには、弊社の営業
            <b>（sales@datalabs.jp）</b>
            に連絡してください。
          </Text>
        </>
      ),
      modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
      closable: false,
      size: 'lg',
      confirmText: 'ログアウト',
      onConfirmPromised: async () => {
        await logout({ logoutParams: { returnTo: window.location.origin } })
        return false
      },
    })
  }, [showModal, logout])

  useEffect(() => {
    if (error) {
      handleUserNotFound()
    }
  }, [error, handleUserNotFound])

  // Store
  const dispatch = useAppDispatch()
  const userLoaded = useSelector((root: RootState) => root.user.userLoaded)
  const serviceStatus = useSelector((state: RootState) => state.system.serviceStatus)

  //* Auth0
  const { user, getAccessTokenSilently } = useAuth0()

  //* ユーザー情報の有無確認
  const refreshAccessToken = useCallback(async () => {
    await getAccessTokenSilently()
  }, [getAccessTokenSilently])

  //* アクセストークン取得
  const getAccessToken = useCallback(
    async (): Promise<string> =>
      getAccessTokenSilently().catch((err) => {
        processErrorHandler(err, ERROR_PROCESS.GET_ACCESS_TOKEN, showErrorModal)
        return ''
      }),
    [getAccessTokenSilently, showErrorModal],
  )

  //* 初回ロード
  useEffect(() => {
    if (!serviceStatus || serviceStatus.isDown.enabled) return

    if (user && !userLoaded) {
      refreshAccessToken()
        .then(async () => {
          const access_token = await getAccessToken()
          if (!access_token) {
            return
          }

          const userInfo = await getUser(access_token, (message: string) => {
            if (message === '40401') {
              handleUserNotFound()
            } else {
              showErrorModal(message)
            }
          })
          if (!userInfo?.user_id) {
            return
          }

          const deprecatedSubscriptionType = userInfo.subscriptions?.type
          const hasDeprecatedSubscription =
            deprecatedSubscriptionType === SubscriptionType.Base ||
            deprecatedSubscriptionType === SubscriptionType.Features
          const userType = decideUserType(userInfo)

          dispatch(setUser(user))
          dispatch(setUserProfile(userInfo))
          dispatch(setUserType(userType))
          dispatch(setUserLoaded())
          dispatch(setHasDeprecatedSubscription(hasDeprecatedSubscription))

          const queries = new URLSearchParams(window.location.search)
          const isDebug = queries.has('debug')
          dispatch(
            setIsAllowedToDebug(isDebug && (MODE === 'dev' || userInfo.owned_products.includes(ProductTypes.Admin))),
          )

          // mixpanel init
          mixpanel.identify(userInfo.user_id)
          mixpanel.people.set({
            $name: user.email?.split('@')[1],
            $email: user.email,
            'User type': userType,
          })
        })
        .catch((err) => {
          processErrorHandler(err, ERROR_PROCESS.GET_ACCESS_TOKEN, showErrorModal)
        })
    }
  }, [
    user,
    userLoaded,
    serviceStatus,
    getAccessToken,
    dispatch,
    refreshAccessToken,
    showErrorModal,
    handleUserNotFound,
  ])

  return {
    getAccessToken,
  }
}
